import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
    Avatar,
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import {
    SearchOutlined as SearchIcon,
    PersonAddOutlined as InviteIcon
} from '@material-ui/icons'

import { send_command } from 'components/api'
import { useAuthentication } from 'components/AuthenticationContext'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'
import { ErrorPop } from 'components/ErrorPop'

import ConfirmActionDialog from './components/ConfirmActionDialog'
import InviteUserDialog from './components/InviteUserDialog'
import UserManagementDrawer from './components/UserManagementDrawer'

function isTrue(value) {
    return String(value).toLowerCase() === 'true'
}

function initials(user) {
    const g = (user.given_name || '')[0] || ''
    const f = (user.family_name || '')[0] || ''
    return (g + f).toUpperCase() || (user.email || '?')[0].toUpperCase()
}

const FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'admin', label: 'Admins' },
    { id: 'super', label: 'Super Admins' },
    { id: 'disabled', label: 'Disabled' }
]

function StatusBadge({ user }) {
    let cls = 'status-active'
    let label = 'Active'
    if (!user.Enabled) {
        cls = 'status-disabled'
        label = 'Disabled'
    } else if (user.UserStatus === 'FORCE_CHANGE_PASSWORD' || user.UserStatus === 'RESET_REQUIRED') {
        cls = 'status-reset'
        label = 'Reset required'
    }
    return (
        <span className={`superadmin-status-badge ${cls}`}>
            <span className="dot" />
            {label}
        </span>
    )
}

function RolesCell({ user }) {
    const admin = isTrue(user['custom:is_admin'])
    const superAdmin = isTrue(user['custom:is_super_admin'])
    if (!admin && !superAdmin) {
        return <span className="superadmin-role-none">User</span>
    }
    return (
        <>
            {superAdmin && <span className="superadmin-role-chip role-super">Super Admin</span>}
            {admin && <span className="superadmin-role-chip role-admin">Admin</span>}
        </>
    )
}

export default function ManageUsers({ tenant_id }) {
    const authState = useAuthentication()
    const [isLoading, setIsLoading] = useState(true)
    const [users, setUsers] = useState([])
    const [error, setError] = useState(null)
    const [success, setSuccess] = useState(null)
    const [search, setSearch] = useState('')
    const [filter, setFilter] = useState('all')
    const [selectedUsername, setSelectedUsername] = useState(null)

    const confirmRef = React.useRef()
    const inviteRef = React.useRef()

    const fetch = useCallback(() => {
        setIsLoading(true)
        const command = tenant_id
            ? send_command('ADMIN_LIST_TENANT_USERS', { tenant_id })
            : send_command('ADMIN_LIST_ALL_USERS', {})
        return command
            .then(re => {
                setUsers(Array.isArray(re) ? re : [])
                setError(null)
            })
            .catch(er => {
                setError(getApiErrorMessage(er, 'Failed to load users'))
                setUsers([])
            })
            .finally(() => setIsLoading(false))
    }, [tenant_id])

    useEffect(() => {
        fetch()
    }, [fetch])

    const currentTenantId = authState?.attributes?.['custom:tenantid']
    const currentTenantLabel = useMemo(() => {
        const match = users.find(u => u['custom:tenantid'] === currentTenantId)
        return match?.tenant_name || currentTenantId || 'Current tenant'
    }, [users, currentTenantId])

    const filteredUsers = useMemo(() => {
        const q = search.trim().toLowerCase()
        return users.filter(u => {
            if (filter === 'admin' && !isTrue(u['custom:is_admin'])) return false
            if (filter === 'super' && !isTrue(u['custom:is_super_admin'])) return false
            if (filter === 'disabled' && u.Enabled) return false
            if (!q) return true
            const haystack = `${u.given_name || ''} ${u.family_name || ''} ${u.email || ''}`.toLowerCase()
            return haystack.includes(q)
        })
    }, [users, search, filter])

    const selectedUser = useMemo(
        () => users.find(u => u.Username === selectedUsername) || null,
        [users, selectedUsername]
    )

    return (
        <>
            <div className="superadmin-user-toolbar">
                <TextField
                    className="user-search"
                    placeholder="Search by name or email…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    variant="outlined"
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                />
                <div className="user-filter-chips">
                    {FILTERS.map(f => (
                        <Chip
                            key={f.id}
                            label={f.label}
                            clickable
                            color={filter === f.id ? 'primary' : 'default'}
                            variant={filter === f.id ? 'default' : 'outlined'}
                            onClick={() => setFilter(f.id)}
                            size="small"
                        />
                    ))}
                </div>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<InviteIcon />}
                    onClick={() => inviteRef.current.open()}
                >
                    Invite User
                </Button>
            </div>

            {error && (
                <div style={{ marginBottom: 12 }}>
                    <Typography color="error" variant="body2">{error}</Typography>
                    <Button size="small" onClick={fetch} style={{ marginTop: 4 }}>Retry</Button>
                </div>
            )}

            <div className="superadmin-card superadmin-user-table">
                {isLoading ? (
                    <div className="user-table-loading"><CircularProgress /></div>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>User</TableCell>
                                    <TableCell>Tenant</TableCell>
                                    <TableCell>Roles</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredUsers.map(u => (
                                    <TableRow key={u.Username} hover>
                                        <TableCell>
                                            <div className="user-cell">
                                                <Avatar className={`user-avatar ${u.Enabled ? '' : 'disabled'}`}>
                                                    {initials(u)}
                                                </Avatar>
                                                <div>
                                                    <div className="uname">{u.family_name}, {u.given_name}</div>
                                                    <div className="uemail">{u.email}</div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>{u.tenant_name || '—'}</TableCell>
                                        <TableCell><RolesCell user={u} /></TableCell>
                                        <TableCell><StatusBadge user={u} /></TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => setSelectedUsername(u.Username)}
                                            >
                                                Manage
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredUsers.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" className="user-table-empty">
                                            No users match your filters.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>

            <UserManagementDrawer
                user={selectedUser}
                open={!!selectedUser}
                onClose={() => setSelectedUsername(null)}
                onChanged={fetch}
                onToast={setSuccess}
                onError={setError}
                confirmRef={confirmRef}
            />

            <InviteUserDialog
                ref={inviteRef}
                tenantLabel={currentTenantLabel}
                onInvited={email => {
                    setSuccess(`Invite sent to ${email}`)
                    fetch()
                }}
                onError={setError}
            />

            <ConfirmActionDialog ref={confirmRef} />

            <ErrorPop error={error} setError={setError} timeout={6000} />
            <Snackbar
                open={success != null}
                autoHideDuration={4000}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                onClose={() => setSuccess(null)}
            >
                <Alert onClose={() => setSuccess(null)} severity="success" variant="filled">
                    {success}
                </Alert>
            </Snackbar>
        </>
    )
}
